/**
 * SurveyQuestionInput
 *
 * Renders the answer widget for a survey's `type`, calling onChange(value) when
 * the user picks. Keeps SurveyFloatingCard's flow logic separate from the
 * per-type UI so new types are a local addition here.
 *
 *   thumbs -> 'up' | 'down'
 *   rating -> 1..(config.max || 5)
 *   nps    -> 0..10
 *   choice -> the selected option's value (from config.options)
 *   text   -> null (the free-text box in the card IS the answer)
 *
 * Props: { type, config, value, onChange, disabled }
 */
import { MaterialCommunityIcons } from '@expo/vector-icons';
import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';

const ACCENT = '#538AF9';

// choice options accept "Label" or { value, label }.
const normalizeOptions = (options) =>
  (Array.isArray(options) ? options : [])
    .map((o) =>
      typeof o === 'string'
        ? { value: o, label: o }
        : o && o.value != null
          ? { value: o.value, label: o.label ?? String(o.value) }
          : null
    )
    .filter(Boolean);

const SurveyQuestionInput = ({ type, config = {}, value, onChange, disabled, compact = false }) => {
  if (type === 'thumbs') {
    const opts = [
      { v: 'up', icon: 'thumb-up', outline: 'thumb-up-outline', label: 'Yes' },
      { v: 'down', icon: 'thumb-down', outline: 'thumb-down-outline', label: 'No' },
    ];
    if (compact) {
      // Minimalist icon-only thumbs for the inline one-line bar.
      return (
        <View style={styles.compactRow}>
          {opts.map((o) => {
            const selected = value === o.v;
            return (
              <Pressable
                key={o.v}
                onPress={() => !disabled && onChange(o.v)}
                hitSlop={8}
                accessibilityRole="button"
                accessibilityLabel={o.label}
                accessibilityState={{ selected }}
              >
                <MaterialCommunityIcons
                  name={selected ? o.icon : o.outline}
                  size={22}
                  color={selected ? (o.v === 'up' ? '#3AE869' : '#FF7B00') : 'rgba(255,255,255,0.7)'}
                />
              </Pressable>
            );
          })}
        </View>
      );
    }
    return (
      <View style={styles.thumbRow}>
        {opts.map((o) => {
          const selected = value === o.v;
          return (
            <Pressable
              key={o.v}
              onPress={() => !disabled && onChange(o.v)}
              style={[styles.thumbBtn, selected && styles.selected]}
              accessibilityRole="button"
              accessibilityLabel={o.label}
              accessibilityState={{ selected }}
            >
              <MaterialCommunityIcons
                name={selected ? o.icon : o.outline}
                size={20}
                color="#FFFFFF"
              />
              <Text style={styles.thumbLabel}>{o.label}</Text>
            </Pressable>
          );
        })}
      </View>
    );
  }

  if (type === 'rating') {
    const max = Number(config.max) > 0 ? Math.floor(Number(config.max)) : 5;
    const stars = Array.from({ length: max }, (_, i) => i + 1);
    if (compact) {
      return (
        <View style={styles.compactStars}>
          {stars.map((n) => (
            <Pressable
              key={n}
              onPress={() => !disabled && onChange(n)}
              hitSlop={2}
              accessibilityRole="button"
              accessibilityLabel={`${n} star${n > 1 ? 's' : ''}`}
              accessibilityState={{ selected: value === n }}
            >
              <MaterialCommunityIcons
                name={value != null && n <= value ? 'star' : 'star-outline'}
                size={20}
                color={value != null && n <= value ? '#FFC53D' : 'rgba(255,255,255,0.4)'}
              />
            </Pressable>
          ))}
        </View>
      );
    }
    return (
      <View>
        <View style={styles.starRow}>
          {stars.map((n) => (
            <Pressable
              key={n}
              onPress={() => !disabled && onChange(n)}
              hitSlop={4}
              accessibilityRole="button"
              accessibilityLabel={`${n} star${n > 1 ? 's' : ''}`}
              accessibilityState={{ selected: value === n }}
            >
              <MaterialCommunityIcons
                name={value != null && n <= value ? 'star' : 'star-outline'}
                size={30}
                color={value != null && n <= value ? '#FFC53D' : 'rgba(255,255,255,0.4)'}
              />
            </Pressable>
          ))}
        </View>
        {(config.lowLabel || config.highLabel) && (
          <View style={styles.scaleLabels}>
            <Text style={styles.scaleLabel}>{config.lowLabel || ''}</Text>
            <Text style={styles.scaleLabel}>{config.highLabel || ''}</Text>
          </View>
        )}
      </View>
    );
  }

  if (type === 'nps') {
    const nums = Array.from({ length: 11 }, (_, i) => i); // 0..10
    return (
      <View>
        <View style={styles.npsRow}>
          {nums.map((n) => {
            const selected = value === n;
            return (
              <Pressable
                key={n}
                onPress={() => !disabled && onChange(n)}
                style={[styles.npsCell, selected && styles.npsCellSelected]}
                accessibilityRole="button"
                accessibilityLabel={`${n}`}
                accessibilityState={{ selected }}
              >
                <Text style={[styles.npsText, selected && styles.npsTextSelected]}>{n}</Text>
              </Pressable>
            );
          })}
        </View>
        <View style={styles.scaleLabels}>
          <Text style={styles.scaleLabel}>{config.lowLabel || 'Not likely'}</Text>
          <Text style={styles.scaleLabel}>{config.highLabel || 'Very likely'}</Text>
        </View>
      </View>
    );
  }

  if (type === 'choice') {
    const options = normalizeOptions(config.options);
    return (
      <View style={styles.choiceList}>
        {options.map((o) => {
          const selected = value === o.value;
          return (
            <Pressable
              key={String(o.value)}
              onPress={() => !disabled && onChange(o.value)}
              style={[styles.choiceBtn, selected && styles.selected]}
              accessibilityRole="button"
              accessibilityLabel={o.label}
              accessibilityState={{ selected }}
            >
              <Text style={styles.choiceText}>{o.label}</Text>
            </Pressable>
          );
        })}
      </View>
    );
  }

  // type === 'text' (or unknown): the card's free-text box is the answer.
  return null;
};

const styles = StyleSheet.create({
  selected: {
    borderColor: ACCENT,
    backgroundColor: 'rgba(36,102,237,0.15)',
  },
  // Compact (inline one-line bar): icon-only, no boxes.
  compactRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  compactStars: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  thumbRow: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 14,
  },
  thumbBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 11,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    backgroundColor: 'rgba(255,255,255,0.04)',
  },
  thumbLabel: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 13,
    color: '#FFFFFF',
  },
  starRow: {
    flexDirection: 'row',
    gap: 6,
    marginTop: 14,
    justifyContent: 'center',
  },
  npsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 5,
    marginTop: 14,
    justifyContent: 'space-between',
  },
  npsCell: {
    width: 26,
    height: 30,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    backgroundColor: 'rgba(255,255,255,0.04)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  npsCellSelected: {
    borderColor: ACCENT,
    backgroundColor: ACCENT,
  },
  npsText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 12,
    color: 'rgba(255,255,255,0.75)',
  },
  npsTextSelected: {
    color: '#FFFFFF',
  },
  scaleLabels: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 6,
  },
  scaleLabel: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 10.5,
    color: 'rgba(255,255,255,0.45)',
  },
  choiceList: {
    marginTop: 14,
    gap: 8,
  },
  choiceBtn: {
    paddingVertical: 11,
    paddingHorizontal: 14,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    backgroundColor: 'rgba(255,255,255,0.04)',
  },
  choiceText: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 13.5,
    color: '#FFFFFF',
  },
});

export default SurveyQuestionInput;
